import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/i18n";
import { Loader2, Printer, Home as HomeIcon, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function QrMock({ code }) {
  const cells = [];
  for (let i = 0; i < 81; i++) {
    const c = code.charCodeAt(i % code.length);
    cells.push((c * 7 + i * 13) % 3 === 0);
  }
  return (
    <div className="grid grid-cols-9 gap-[2px] w-24 h-24 p-2 bg-white rounded-lg" data-testid="ticket-qr">
      {cells.map((on, i) => (
        <span key={i} className={on ? "bg-night" : "bg-white"} />
      ))}
    </div>
  );
}

export default function PaymentSuccess() {
  const { t, locale } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [booking, setBooking] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const tries = useRef(0);

  useEffect(() => {
    if (!sessionId) return;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/bookings/by-session/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setBooking(res.data.booking);
          return;
        }
      } catch (e) { /* keep polling */ }
      tries.current += 1;
      if (tries.current >= 20) setTimedOut(true);
      else setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  const fmtDate = (iso) =>
    new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(iso + "T12:00:00"));
  const fmtMoney = (n) => new Intl.NumberFormat(locale, { style: "currency", currency: "EUR" }).format(n);

  return (
    <main className="min-h-screen bg-night flex items-center justify-center px-5 py-20" data-testid="payment-success-page">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[30rem] h-[30rem] rounded-full bg-sunset/10 blur-[130px] pointer-events-none" />
      {!booking ? (
        <div className="text-center space-y-4">
          <Loader2 size={32} className="animate-spin text-sunset mx-auto" />
          <p className="font-mono text-sm text-slate-400" data-testid="verifying-payment">{timedOut ? t.success.pending : t.success.verifying}</p>
        </div>
      ) : (
        <div className="w-full max-w-md space-y-6">
          <div id="ticket-pass" data-testid="booking-confirmation-pass"
            className="relative rounded-3xl border border-sunset/40 bg-surface overflow-hidden shadow-[0_0_80px_rgba(255,94,26,0.2)]">
            <div className="bg-gradient-to-r from-sunset to-flame px-7 py-5 flex items-center justify-between">
              <img src="/assets/logo.webp" alt="" className="w-12 h-12 rounded-full ring-2 ring-night/30 object-cover" />
              <div className="text-right">
                <p className="font-display font-extrabold text-night leading-none">CURAÇAO BEACH BBQ</p>
                <p className="text-night/80 text-[11px] font-mono uppercase tracking-widest mt-1">{t.success.title}</p>
              </div>
            </div>
            <div className="px-7 py-6 space-y-5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.success.code}</p>
                  <p className="font-mono text-2xl font-bold text-sunset tracking-wider" data-testid="booking-code">{booking.booking_code}</p>
                </div>
                <QrMock code={booking.booking_code} />
              </div>
              <div className="grid grid-cols-2 gap-4 border-t border-stroke pt-5 text-sm">
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.success.date}</p>
                  <p className="font-semibold mt-1 capitalize" data-testid="ticket-date">{fmtDate(booking.date)}</p>
                </div>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.success.guests}</p>
                  <p className="font-semibold mt-1" data-testid="ticket-guests">{booking.guests}</p>
                </div>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.success.name}</p>
                  <p className="font-semibold mt-1" data-testid="ticket-name">{booking.name}</p>
                </div>
                <div>
                  <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{t.success.total}</p>
                  <p className="font-semibold mt-1 text-ocean" data-testid="ticket-total">{fmtMoney(booking.amount)}</p>
                </div>
              </div>
              <p className="text-xs text-slate-500 italic border-t border-dashed border-stroke pt-4">{t.success.sub}</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button onClick={() => window.print()} data-testid="print-ticket-button"
              className="flex-1 flex items-center justify-center gap-2 rounded-full border border-stroke bg-surface py-3 text-sm font-semibold hover:border-sunset hover:text-sunset transition-colors duration-300">
              <Printer size={15} /> {t.success.print}
            </button>
            <Link to="/" data-testid="back-home-button"
              className="flex-1 flex items-center justify-center gap-2 rounded-full bg-sunset text-night py-3 text-sm font-bold hover:bg-flame transition-colors duration-300">
              <HomeIcon size={15} /> {t.success.home}
            </Link>
          </div>
          <p className="text-center text-[11px] font-mono uppercase tracking-widest text-slate-600 flex items-center justify-center gap-1.5">
            <Flame size={11} className="text-sunset" /> Curaçao Beach Barbecue
          </p>
        </div>
      )}
    </main>
  );
}
